/**
 * Rejting radnika 1–100 (bez valute — pogodno za međunarodnu upotrebu).
 */

export const DEFAULT_RATING_PARAMS = {
  completedWeight: 30,
  firstVisitWeight: 25,
  profitEfficiencyWeight: 20,
  addressCoverageWeight: 10,
  complaintPenalty: 8,
  secondVisitPenalty: 5,
  applyCoefficient: true
};

export const RATING_STORAGE_KEY = 'workerRatingParams';

export const loadRatingParams = () => {
  try {
    const saved = localStorage.getItem(RATING_STORAGE_KEY);
    if (saved) return { ...DEFAULT_RATING_PARAMS, ...JSON.parse(saved) };
  } catch (e) {
    console.error('workerRatingParams parse error', e);
  }
  return { ...DEFAULT_RATING_PARAMS };
};

export const saveRatingParams = (params) => {
  localStorage.setItem(RATING_STORAGE_KEY, JSON.stringify(params));
};

/**
 * @param {object} stats — iz computeWorkerMonthStats
 * @param {object} worker
 * @param {object} params — težine iz podešavanja
 * @returns {{ score: number, breakdown: object }}
 */
export const calculateWorkerRating = (stats, worker, params = DEFAULT_RATING_PARAMS) => {
  if (!stats || stats.completedJobs === 0) {
    return { score: 0, breakdown: { empty: true } };
  }

  const capacity = Math.max(1, stats.monthMaxCapacity || 1);
  const completionRatio = Math.min(1, stats.completedJobs / capacity);
  const firstVisitRatio = stats.completedJobs > 0
    ? stats.firstVisitJobs / stats.completedJobs
    : 0;
  const profitMargin = stats.totalRevenue > 0
    ? Math.max(0, (stats.totalRevenue - stats.totalExpenses) / stats.totalRevenue)
    : 0;
  const addressCoverage = Math.min(1, stats.uniqueAddresses / stats.completedJobs);
  const dayCoverage = stats.activeDays > 0
    ? Math.min(1, stats.completedJobs / stats.activeDays)
    : 0;

  const completionPts = completionRatio * (params.completedWeight || 0);
  const firstVisitPts = firstVisitRatio * (params.firstVisitWeight || 0);
  const profitPts = profitMargin * (params.profitEfficiencyWeight || 0);
  const addressPts = ((addressCoverage + dayCoverage) / 2) * (params.addressCoverageWeight || 0);

  const complaintDeduction = stats.complaintsCount * (params.complaintPenalty || 0);
  const secondVisitDeduction = stats.secondVisitJobs * (params.secondVisitPenalty || 0);

  let raw =
    completionPts +
    firstVisitPts +
    profitPts +
    addressPts -
    complaintDeduction -
    secondVisitDeduction;

  if (params.applyCoefficient) {
    const coeff = worker?.specializationCoefficient || stats.coefficient || 1;
    raw *= coeff / 5;
  }

  const score = Math.round(Math.max(1, Math.min(100, raw)));

  return {
    score,
    breakdown: {
      completionPts: Math.round(completionPts),
      firstVisitPts: Math.round(firstVisitPts),
      profitPts: Math.round(profitPts),
      addressPts: Math.round(addressPts),
      complaintDeduction: Math.round(complaintDeduction),
      secondVisitDeduction: Math.round(secondVisitDeduction)
    }
  };
};

export const getRatingColorClass = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-emerald-300';
  if (score >= 40) return 'text-amber-300';
  return 'text-red-400';
};

export const getRatingBarColorClass = (score) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-emerald-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};
